import {
  Crosshair,
  Zap,
  Map,
  ShieldCheck,
  Activity,
  Bell,
  BarChart3,
  ArrowRight,
} from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal } from "@/components/Reveal";
import { scrollToId } from "@/lib/ui";

const MODULES = [
  { icon: Crosshair, title: "MARKET CONTEXT", desc: "Organize qualifying P.E.E. market conditions." },
  { icon: Zap, title: "EVENT DETECTION", desc: "Identify when predefined P.E.E. events occur." },
  { icon: Map, title: "TRADE MAP", desc: "Organize entry, stop-loss, TP1, TP2, and trade information." },
  { icon: ShieldCheck, title: "VALIDATION", desc: "Track whether a developing trade continues to satisfy the process." },
  { icon: Activity, title: "TRADE STATUS", desc: "Follow the trade through its defined execution lifecycle." },
  { icon: Bell, title: "ALERTS", desc: "Stay informed as important trade conditions change." },
  { icon: BarChart3, title: "ANALYTICS", desc: "Measure P.E.E. outcomes and execution patterns over time." },
];

export default function PeeOSV2() {
  return (
    <section id="pee-os" data-testid="pee-os-section" className="border-t border-line bg-[#070809] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          chapter="03 — P.E.E. OS"
          testid="pee-os-heading"
          title={
            <>
              The Game Plan.
              <br />
              Built into an
              <br />
              <span className="glow-text-neon text-neon">execution system.</span>
            </>
          }
          description="Knowing the Game Plan is step one. Executing it consistently is the next challenge. P.E.E. OS™ is the technology layer of the P.E.E. ecosystem. It organizes the information required to execute the Position • Event • Entry methodology into a structured trade lifecycle."
        />

        <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {MODULES.map((m, i) => (
            <Reveal key={m.title} delay={0.05 * i} className="h-full">
              <div
                data-testid={`os-module-${i}`}
                className="group h-full rounded-lg border border-line bg-charcoal p-6 transition-colors duration-500 hover:border-neon/45"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-md border border-line bg-ink transition-colors duration-500 group-hover:border-neon/50">
                  <m.icon size={17} className="text-neon" />
                </div>
                <h3 className="mt-5 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-white">
                  {m.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">{m.desc}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.35} className="h-full">
            <button
              data-testid="os-explore-tile"
              onClick={() => scrollToId("lifecycle")}
              className="group flex h-full w-full flex-col justify-between rounded-lg border border-neon/50 bg-neon/5 p-6 text-left transition-colors duration-500 hover:bg-neon/10"
            >
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-neon">
                See it in motion
              </span>
              <span className="mt-6 flex items-center gap-2 font-display text-lg font-bold uppercase tracking-tight text-white">
                Explore P.E.E. OS
                <ArrowRight size={17} className="text-neon transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </button>
          </Reveal>
        </div>

        <div className="mt-20 grid gap-10 border-t border-line pt-14 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <h3 className="font-display text-3xl font-extrabold uppercase leading-[1.08] tracking-tight text-white sm:text-4xl">
              The OS doesn't
              <br />
              replace the trader.
            </h3>
            <p className="mt-5 max-w-md text-base leading-relaxed text-mist">
              It doesn't remove risk. It doesn't guarantee outcomes. And it
              doesn't eliminate your responsibility to make trading decisions.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <h3 className="font-display text-3xl font-extrabold uppercase leading-[1.08] tracking-tight sm:text-4xl">
              <span className="text-white">It gives your</span>
              <br />
              <span className="glow-text-neon text-neon">execution a structure.</span>
            </h3>
            <button
              data-testid="os-explore-cta"
              onClick={() => scrollToId("lifecycle")}
              className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-neon transition-opacity hover:opacity-70"
            >
              Explore P.E.E. OS
              <ArrowRight size={14} />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
